import React, { useCallback, useEffect, useMemo, useState } from "react";
import Header from "@/components/Header";
import ActivityFeed from "@/components/ActivityFeed";
import { useLang } from "@/i18n";
import api from "@/lib/api";
import { Search, PlusCircle, Pencil, Layers } from "lucide-react";

const RANGES = [
  { id: "all", de: "Gesamt", en: "All time" },
  { id: "1h", de: "1 Std.", en: "1h", ms: 3600e3 },
  { id: "24h", de: "24 Std.", en: "24h", ms: 24 * 3600e3 },
  { id: "7d", de: "7 Tage", en: "7 days", ms: 7 * 24 * 3600e3 },
  { id: "30d", de: "30 Tage", en: "30 days", ms: 30 * 24 * 3600e3 },
];

export default function Activity() {
  const { t, lang } = useLang();
  const de = lang === "de";
  const [events, setEvents] = useState([]);
  const [typeF, setTypeF] = useState("all");
  const [range, setRange] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const e = await api.get("/events?limit=500");
      setEvents(e.data);
    } catch (err) {
      // interceptor handles auth
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [load]);

  const filtered = useMemo(() => {
    let list = events;
    if (typeF === "registered") list = list.filter((e) => e.type === "registered");
    else if (typeF === "changed") list = list.filter((e) => e.type !== "registered");
    const r = RANGES.find((x) => x.id === range);
    if (r && r.ms) {
      const cutoff = Date.now() - r.ms;
      list = list.filter((e) => new Date(e.timestamp).getTime() >= cutoff);
    }
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      list = list.filter((e) =>
        [e.device_code, e.hostname, e.field, e.old_value, e.new_value]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(s))
      );
    }
    return list;
  }, [events, typeF, range, q]);

  const typeChips = [
    { id: "all", label: de ? "Alle" : "All", Icon: Layers },
    { id: "registered", label: de ? "Registriert" : "Registered", Icon: PlusCircle },
    { id: "changed", label: de ? "Geändert" : "Changed", Icon: Pencil },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <main className="w-full px-4 sm:px-6 py-6 space-y-5">
        <div className="fade-up">
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{t("activity")}</h1>
          <p className="text-sm text-gray-600 mt-1">
            {de ? "Alle Änderungen und Ereignisse der Geräteflotte in Echtzeit." : "All fleet changes and events in real time."}
          </p>
        </div>

        {/* Filter bar */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="activity-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={de ? "Suche: Code, Hostname, Feld, Wert …" : "Search: code, hostname, field, value …"}
              className="w-full bg-white border border-gray-300 rounded-lg pl-9 pr-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-[#c00000] focus:ring-2 focus:ring-[#c00000]/20 outline-none transition-all"
            />
          </div>
          <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg p-1">
            {typeChips.map((c) => (
              <button
                key={c.id}
                data-testid={`activity-type-${c.id}`}
                onClick={() => setTypeF(c.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${typeF === c.id ? "bg-[#c00000] text-white font-medium" : "text-gray-600 hover:bg-gray-100"}`}
              >
                <c.Icon className="w-4 h-4" /> {c.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg p-1">
            {RANGES.map((r) => (
              <button
                key={r.id}
                data-testid={`activity-range-${r.id}`}
                onClick={() => setRange(r.id)}
                className={`px-2.5 py-1.5 rounded-md text-sm transition-colors ${range === r.id ? "bg-gray-900 text-white font-medium" : "text-gray-600 hover:bg-gray-100"}`}
              >
                {de ? r.de : r.en}
              </button>
            ))}
          </div>
        </div>

        <ActivityFeed events={filtered} lang={lang} />
      </main>
    </div>
  );
}
